<template>
  <div
    class="comment"
    :class="{ 'pinned': !!comment.is_pinned }"
  >
    <v-row no-gutters>
      <v-col
        cols="auto"
        class="pr-4"
      >
        <v-avatar
          :size="50"
          color="grey lighten-2"
        >
          <v-icon>
            person
          </v-icon>
        </v-avatar>
      </v-col>
      <v-col class="main-col">
        <v-card
          v-if="!comment.deleted_at"
          class="comment-actions"
          color="white"
          elevation="10"
        >
          <v-tooltip
            v-if="comment.user_id === user.id"
            top
          >
            <template v-slot:activator="{ on }">
              <v-btn
                icon
                @click="$emit('comment:edit', comment)"
                v-on="on"
              >
                <v-icon
                  small
                >
                  edit
                </v-icon>
              </v-btn>
            </template>
            Edit comment
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                icon
                @click="pinComment()"
                v-on="on"
              >
                <v-icon
                  small
                >
                  push_pin
                </v-icon>
              </v-btn>
            </template>
            <span>{{ comment.is_pinned ? 'Unpin comment' : 'Pin comment' }}</span>
          </v-tooltip>
          <v-divider vertical />
          <v-btn
            v-if="$can('delete-all-comments', 'Comment') || comment.user_id === user.id"
            icon
            @click="deleteConfirmationDialogVisible = true"
          >
            <v-icon
              small
              color="red"
            >
              delete
            </v-icon>
          </v-btn>
        </v-card>

        <div class="font-weight-bold text-body-1 blue-grey--text text--darken-3">
          {{ comment.user.name }}
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <span
                class="grey--text caption ml-2"
                v-on="on"
              >
                {{ comment.updated_at | moment('from') }}
              </span>
            </template>
            {{ comment.updated_at | moment('D MMM YYYY HH:mm:ss') }}
          </v-tooltip>
          <span
            v-if="comment.deleted_at"
            class="error--text caption"
          >
            (deleted)
          </span>
          <span
            v-else-if="comment.updated_at !== comment.created_at"
            class="grey--text caption"
          >
            (edited)
          </span>
          <v-chip
            v-if="comment.is_pinned"
            small
            dark
            color="blue-grey darken-4"
            class="ml-3"
          >
            PINNED
          </v-chip>
        </div>
        <div
          class="text-body-1 grey--text text--darken-3 mt-1 comment-body"
          v-html="comment.comment"
        />
        <div
          v-if="$can('see-all-comments', 'Comment')"
          class="mt-2"
        >
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <span
                class="grey--text caption"
                v-on="on"
              >
                <v-icon
                  x-small
                  class="mr-1"
                  :color="comment.is_public ? 'primary' : 'grey'"
                >
                  {{ comment.is_public ? 'visibility' : 'visibility_off' }}
                </v-icon>
                {{ comment.is_public ? 'Visible' : 'Restricted' }}
              </span>
            </template>
            <div v-if="!comment.is_public">
              <div>Comment visible to:</div>
              <ul>
                <li
                  v-for="role in comment.visible_roles"
                  :key="comment.id + '-role-' + role.id"
                >
                  All {{ role.title }}
                </li>
                <li
                  v-for="staff in comment.visible_staff"
                  :key="staff.id + '-staff-' + staff.id"
                >
                  {{ staff.name }}
                </li>
              </ul>
            </div>
            <div v-else>
              Visible to anyone that can view yacht comments
            </div>
          </v-tooltip>
        </div>
      </v-col>
    </v-row>

    <confirm-delete-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirmed="deleteComment()"
    />
  </div>
</template>
<script>
import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import Comments from '@/services/api/Comments'
import { cloneDeep } from 'lodash'

export default {
  name: 'CommentRow',
  components: { ConfirmDeleteDialog },
  mixins: [ deleteConfirmationMixin ],
  props: {
    comment: {
      type: Object,
      required: true
    }
  },
  computed: {
    user() {
      return this.$store.state.auth.user
    }
  },
  methods: {
    async deleteComment() {
      try {
        this.deleting = true

        await Comments.destroy(this.comment.id)

        this.$emit('comment:deleted')

        this.$store.dispatch('alert', {
          text: 'Comment deleted',
          color: 'success'
        })
      } catch (e) {
        throw e
      } finally {
        this.deleting = false
        this.deleteConfirmationDialogVisible = false
      }
    },
    async pinComment() {
      this.$store.dispatch('alert', {
        text: this.comment.is_pinned ? 'Comment unpinned' : 'Comment pinned',
        color: 'success'
      })

      let newComment = cloneDeep(this.comment)
      newComment.is_pinned = !this.comment.is_pinned

      this.$emit('comment:updated', newComment)

      try {
        await Comments.togglePin(this.comment.id)
      } catch (e) {
        throw e
      }
    }
  }
}
</script>
<style lang="scss">
$color-black: #000000;
$color-white: #ffffff;

.comment-body p:last-child {
  margin-bottom: 0;
}

.comment {
  margin: -10px;
  padding: 10px;

  &.pinned {
    border: 2px solid currentColor;
    border-radius: 10px;
  }

  &:hover {
    background-color: rgba(0,0,0,0.02);

    .comment-actions {
      opacity: 1;
    }
  }

  .main-col {
    position: relative;
  }

  .comment-actions {
    opacity: 0;
    position: absolute;
    right: 5px;
    top: 5px;
  }
}
</style>
